/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Paper } from "@mui/material";
import { styles } from "../../themes/dashboard";
import {
  StatisticsTitle,
  StatisticsValue,
  CardContainer,
  IconContainer,
  InformationContainer,
  TitleContainer,
  LightTooltip,
  StyledInfoIcon,
} from "../statistics_cards/styles";
import { StatisticSkeletalLoader } from "../statistic_skeletal_loader";

const DEFAULT_COLUMNS = {
  xs: 2,
  sm: 2,
  md: 3,
  lg: 4,
};

const DEFAULT_BORDER_COLOR = "#eee";

const getRowMeta = (index, totalItems, columns) => {
  const rowStart = Math.floor(index / columns) * columns;
  const itemsInRow = Math.min(columns, totalItems - rowStart);
  const positionInRow = index - rowStart;

  return {
    itemsInRow,
    isFirstRow: rowStart === 0,
    isFirstColumn: positionInRow === 0,
    isLastRow: rowStart + columns >= totalItems,
    isLastColumn: positionInRow === columns - 1,
  };
};

const buildResponsiveValue = (index, totalItems, columns, resolver) => {
  return Object.entries(columns).reduce((responsiveStyles, [breakpoint, count]) => {
    responsiveStyles[breakpoint] = resolver({
      columns: count,
      ...getRowMeta(index, totalItems, count),
    });

    return responsiveStyles;
  }, {});
};

const getCardBorderStyles = (index, totalItems, columns, borderColor) => {
  const baseStyles = {
    borderRight: buildResponsiveValue(index, totalItems, columns, ({ isLastColumn }) =>
      isLastColumn ? "none" : `1px solid ${borderColor}`
    ),
    borderBottom: buildResponsiveValue(index, totalItems, columns, ({ isLastRow }) =>
      isLastRow ? "none" : `1px solid ${borderColor}`
    ),
  };

  return {
    ...baseStyles,
    borderTopLeftRadius: buildResponsiveValue(index, totalItems, columns, ({ isFirstRow, isFirstColumn }) =>
      isFirstRow && isFirstColumn ? 10 : 0
    ),
    borderTopRightRadius: buildResponsiveValue(index, totalItems, columns, ({ isFirstRow, isLastColumn }) =>
      isFirstRow && isLastColumn ? 10 : 0
    ),
    borderBottomLeftRadius: buildResponsiveValue(index, totalItems, columns, ({ isLastRow, isFirstColumn }) =>
      isLastRow && isFirstColumn ? 10 : 0
    ),
    borderBottomRightRadius: buildResponsiveValue(index, totalItems, columns, ({ isLastRow, isLastColumn }) =>
      isLastRow && isLastColumn ? 10 : 0
    ),
  };
};

const resolveColumns = (columns = {}) => ({
  ...DEFAULT_COLUMNS,
  ...columns,
});

const resolveSx = (sx, theme) => {
  if (typeof sx === "function") {
    return sx(theme);
  }

  return sx || {};
};

export const KpiCardGrid = ({
  config = [],
  data = {},
  loading = false,
  isMobile = false,
  columns,
  showBorders = true,
  borderColor = DEFAULT_BORDER_COLOR,
  containerTestId,
  containerSx,
  getCardProps,
}) => {
  const resolvedColumns = resolveColumns(columns);

  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: Object.entries(resolvedColumns).reduce(
          (templateColumns, [breakpoint, count]) => ({
            ...templateColumns,
            [breakpoint]: `repeat(${count}, 1fr)`,
          }),
          {}
        ),
        borderRadius: 2,
        marginTop: "20px",
        boxShadow: styles?.boxShadow,
        overflow: "hidden",
        ...resolveSx(containerSx, theme),
      })}
      data-testid={containerTestId}
    >
      {config.map((item, index) => {
        const rawValue = item.getValue ? item.getValue(data, item) : data?.[item.key];
        const value = item.formatValue ? item.formatValue(rawValue, data, item) : rawValue;
        const cardProps = getCardProps ? getCardProps(item, index) : item.cardProps || {};
        const { sx: cardSx, ...paperProps } = cardProps;

        return (
          <Paper
            key={item.id || item.key || index}
            elevation={0}
            square
            data-testid={item.cardTestId}
            sx={(theme) => ({
              minHeight: 73,
              backgroundColor: "white",
              padding: isMobile ? "12px" : "5px 20px",
              ...(showBorders
                ? getCardBorderStyles(index, config.length, resolvedColumns, borderColor)
                : {}),
              ...resolveSx(item.cardSx, theme),
              ...resolveSx(cardSx, theme),
            })}
            {...paperProps}
          >
            <CardContainer isMobile={isMobile}>
              <IconContainer>{item.icon}</IconContainer>
              <InformationContainer isMobile={isMobile}>
                <TitleContainer>
                  <StatisticsTitle data-testid={item.titleTestId}>{item.title}</StatisticsTitle>
                  {item.description ? (
                    <LightTooltip title={item.description} placement={item.tooltipPlacement || "top"}>
                      <StyledInfoIcon data-testid={item.tooltipTestId} />
                    </LightTooltip>
                  ) : null}
                </TitleContainer>
                {loading ? (
                  <StatisticSkeletalLoader dataTestId={item.loaderTestId} />
                ) : (
                  <StatisticsValue data-testid={item.valueTestId}>{value}</StatisticsValue>
                )}
              </InformationContainer>
            </CardContainer>
          </Paper>
        );
      })}
    </Box>
  );
};
